import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

// Global store for dynamic breadcrumb overrides
const dynamicLabels = ref(new Map());

/**
 * Composable for generating dynamic breadcrumbs from Vue Router
 * Supports custom labels, icons, route hierarchy, and logical parents
 *
 * Route Meta Options:
 * - breadcrumbLabel: Custom label (fallback: title, then route name)
 * - breadcrumbIcon: Icon component to display
 * - breadcrumbParent: Route name to use as logical parent (creates hierarchy)
 * - breadcrumb: Set to false to exclude from breadcrumbs
 * - breadcrumbClickable: Set to false to disable navigation
 */
export function useBreadcrumbs() {
    const route = useRoute();
    const router = useRouter();

    /**
     * Set a dynamic label for a specific route
     * This allows components to override breadcrumb labels after data loads
     */
    const setDynamicLabel = (routeName, label) => {
        dynamicLabels.value.set(routeName, label);
    };

    /**
     * Clear dynamic label for a route
     */
    const clearDynamicLabel = (routeName) => {
        dynamicLabels.value.delete(routeName);
    };

    /**
     * Generate dynamic label for breadcrumb item
     * Supports function-based labels for dynamic content and dynamic overrides
     */
    const generateLabel = (meta, currentRoute, fallbackName, routeName) => {
        const { breadcrumbLabel, title } = meta;

        // Check for dynamic override first
        const dynamicLabel = dynamicLabels.value.get(routeName);
        if (dynamicLabel) {
            return dynamicLabel;
        }

        // If breadcrumbLabel is a function, call it with route context
        if (typeof breadcrumbLabel === 'function') {
            try {
                return breadcrumbLabel(currentRoute, router);
            } catch (error) {
                console.warn('Error generating dynamic breadcrumb label:', error);
                return title || fallbackName;
            }
        }

        // Otherwise use static label
        return breadcrumbLabel || title || fallbackName;
    };

    /**
     * Generate breadcrumb items from current route and its matched routes
     */
    const breadcrumbs = computed(() => {
        const matched = route.matched.filter((record) => record.meta?.breadcrumb !== false);
        const items = [];

        // Build breadcrumb chain including logical parents
        const breadcrumbChain = [];

        // Start with matched routes (actual hierarchy)
        matched.forEach((record) => {
            if (record.meta?.breadcrumb !== false) {
                breadcrumbChain.push(record);
            }
        });

        // If we have breadcrumb parents defined, insert them
        const currentRoute = matched[matched.length - 1];
        if (currentRoute?.meta?.breadcrumbParent) {
            let parentRouteName = currentRoute.meta.breadcrumbParent;
            
            // Support function-based breadcrumbParent for dynamic parent determination
            if (typeof parentRouteName === 'function') {
                try {
                    parentRouteName = parentRouteName(route);
                } catch (error) {
                    console.warn('Error executing breadcrumbParent function:', error);
                    parentRouteName = null;
                }
            }
            
            const parentRoute = router.getRoutes().find((r) => r.name === parentRouteName);

            if (parentRoute && !breadcrumbChain.some((r) => r.name === parentRoute.name)) {
                // Insert parent before the current route
                breadcrumbChain.splice(-1, 0, parentRoute);
            }
        }

        breadcrumbChain.forEach((record, index) => {
            const routeName = record.name;
            const routePath = record.path;
            const meta = record.meta || {};
            const isLast = index === breadcrumbChain.length - 1;

            // Generate breadcrumb item
            const item = {
                name: routeName,
                path: routePath,
                label: generateLabel(meta, route, routeName, routeName),
                icon: meta.breadcrumbIcon || meta.icon || null,
                isActive: isLast,
                isClickable: !isLast && meta.breadcrumbClickable !== false,
                params: route.params,
                query: routeName === 'add-connectors' ? {
                    ...route.query,
                    // Set tab based on current route context
                    tab: route.path.includes('/destinations') ? 'Destinations' : 'Sources'
                } : route.query,
                // Custom properties for slots/components
                component: meta.breadcrumbComponent || null,
                props: meta.breadcrumbProps || {},
                slots: meta.breadcrumbSlots || {}
            };

            items.push(item);
        });

        return items;
    });

    /**
     * Navigate to a breadcrumb item
     */
    const navigateTo = (item) => {
        if (item.isClickable && !item.isActive) {
            router.push({
                name: item.name,
                params: item.params,
                query: item.query
            });
        }
    };

    /**
     * Get the current page title (last breadcrumb item)
     */
    const currentPageTitle = computed(() => {
        const lastItem = breadcrumbs.value[breadcrumbs.value.length - 1];
        return lastItem?.label || '';
    });

    /**
     * Check if breadcrumbs should be shown
     */
    const shouldShowBreadcrumbs = computed(() => {
        return breadcrumbs.value.length > 0 && route.meta?.hideBreadcrumbs !== true;
    });

    return {
        breadcrumbs,
        navigateTo,
        currentPageTitle,
        shouldShowBreadcrumbs,
        setDynamicLabel,
        clearDynamicLabel
    };
}
